import type { CustomerOrderStatus, GetApiCustomerOrdersData } from '@/api';
import type { TableSort } from '@/ui';

import { formatSort, isSameSort, parseSort } from '../../../lib/tableSort';

/*
 * Состояние списка заказов 1С в search params маршрута. Период - даты дня
 * (2026-09-01) по местному времени, обе границы включительно; в запрос уходят
 * моменты времени: начало первого дня и начало дня после последнего.
 */

export const PAGE_SIZE = 20;

export type ActivityFilter = 'active' | 'inactive' | 'all';

/** Поля, по которым бэкенд разрешает сортировать (SearchCustomerOrdersQuery.SortFields). */
export const SORT_FIELDS = [
  'date',
  'number',
  'amount',
  'shipmentDate',
] as const;

const DEFAULT_SORT: TableSort = { field: 'date', dir: 'desc' };
const DEFAULT_ACTIVITY: ActivityFilter = 'active';

const STATUSES: ReadonlyArray<CustomerOrderStatus> = [
  'NotAgreed',
  'ToFulfil',
  'ToShip',
  'Closed',
];

export interface CustomerOrdersSearch {
  /** Идентификатор партнёра-клиента в Culture. */
  partner?: string;
  from?: string;
  to?: string;
  status?: CustomerOrderStatus;
  search?: string;
  activity?: ActivityFilter;
  page?: number;
  sort?: string;
}

export interface ResolvedCustomerOrdersSearch {
  partner: string | undefined;
  from: string;
  to: string;
  status: CustomerOrderStatus | undefined;
  search: string;
  activity: ActivityFilter;
  page: number;
  sort: TableSort;
}

const isActivityFilter = (value: unknown): value is ActivityFilter =>
  value === 'active' || value === 'inactive' || value === 'all';

const isStatus = (value: unknown): value is CustomerOrderStatus =>
  STATUSES.includes(value as CustomerOrderStatus);

const isDay = (value: unknown): value is string =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);

export const validateCustomerOrdersSearch = (
  raw: Record<string, unknown>,
): CustomerOrdersSearch => {
  const result: CustomerOrdersSearch = {};

  if (typeof raw.partner === 'string' && raw.partner !== '')
    result.partner = raw.partner;
  if (isDay(raw.from)) result.from = raw.from;
  if (isDay(raw.to)) result.to = raw.to;
  if (isStatus(raw.status)) result.status = raw.status;

  const search = typeof raw.search === 'string' ? raw.search.trim() : '';
  if (search !== '') result.search = search;

  if (isActivityFilter(raw.activity) && raw.activity !== DEFAULT_ACTIVITY)
    result.activity = raw.activity;

  const page = Number(raw.page);
  if (Number.isInteger(page) && page > 1) result.page = page;

  const sort = parseSort(raw.sort, SORT_FIELDS);
  if (sort && !isSameSort(sort, DEFAULT_SORT)) result.sort = formatSort(sort);

  return result;
};

export const resolveCustomerOrdersSearch = (
  search: CustomerOrdersSearch,
): ResolvedCustomerOrdersSearch => ({
  partner: search.partner,
  from: search.from ?? '',
  to: search.to ?? '',
  status: search.status,
  search: search.search ?? '',
  activity: search.activity ?? DEFAULT_ACTIVITY,
  page: search.page ?? 1,
  sort: parseSort(search.sort, SORT_FIELDS) ?? DEFAULT_SORT,
});

/** Начало дня по местному времени как момент времени; `days` сдвигает день. */
const startOfDay = (day: string, days = 0): string => {
  const date = new Date(`${day}T00:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString();
};

export const toCustomerOrdersQuery = (
  resolved: ResolvedCustomerOrdersSearch,
): NonNullable<GetApiCustomerOrdersData['query']> => {
  const query: NonNullable<GetApiCustomerOrdersData['query']> = {
    PageNumber: resolved.page,
    PageSize: PAGE_SIZE,
    Sort: formatSort(resolved.sort),
  };

  if (resolved.partner !== undefined) query.PartnerId = resolved.partner;
  if (resolved.from !== '') query.DateFrom = startOfDay(resolved.from);
  // Бэкенд исключает правую границу - отправляем начало следующего дня.
  if (resolved.to !== '') query.DateTo = startOfDay(resolved.to, 1);
  if (resolved.status !== undefined) query.Statuses = [resolved.status];
  if (resolved.search !== '') query.Search = resolved.search;

  // Бэкенд по умолчанию отдаёт действующие - «действующие» не отправляем.
  if (resolved.activity === 'inactive') query.Activity = 'Inactive';
  if (resolved.activity === 'all') query.Activity = 'All';

  return query;
};
